<template>
  <v-modal
    v-if="showModal"
    vertical
    modal-class="completable-pages-modal"
    @close="() => (this.showModal = false)"
  >
    <template :slot="COMPLETABLE_PAGES_MODAL_HASH">
      <div v-if="isLoading" class="d-flex justify-content-center">
        <v-loader />
      </div>
      <template v-else>
        <div
          class="pl-2 py-3 overview d-flex justify-content-between align-items-center"
        >
          <div class="overview__title">Overview</div>
          <completion-percent
            :percent-complete="progress.percent_complete || 0"
            class="m-0"
          />
        </div>

        <v-link
          class="page-home page-progress d-flex justify-content-between align-items-center px-2 py-3"
          @click="() => goToPath(homePagePath)"
        >
          <div class="page-title">{{ homePageTitle }}</div>
          <v-icon icon="home" size="1.8rem" />
        </v-link>

        <v-link
          v-for="page in sequenceList"
          with-access-checks
          :key="page.page_id"
          :page-id="page.page_id"
          :to="page.path"
          class="page-progress p-relative d-block px-2 py-3"
        >
          <template v-slot="{ isLocked, isLoadingAccess, availableAfter }">
            <div class="page-title">{{ page.title }}</div>

            <template v-if="isLocked || isLoadingAccess">
              <access-lock
                horizontal
                :is-loading="isLoadingAccess"
                :available-after="availableAfter"
              />
            </template>
            <template v-else>
              <complete-check horizontal :complete="page.is_completed" />
            </template>
          </template>
        </v-link>
      </template>
    </template>
  </v-modal>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import EventBus, { EVENTS } from "@/lib/event-bus"

import { isPresent } from "@/lib/utils"

export const COMPLETABLE_PAGES_MODAL_HASH = "#overview"

export default {
  components: {
    CompleteCheck: () => import("@/components/collection-item/CompleteCheck"),
    CompletionPercent: () =>
      import("@/components/page-actions/progress/CompletionPercent"),
    AccessLock: () => import("@/components/AccessLock")
  },

  data() {
    return {
      showModal: false,
      isLoading: false,
      sequence: [],
      progress: {},
      COMPLETABLE_PAGES_MODAL_HASH
    }
  },

  mounted() {
    EventBus.$on(EVENTS.OPEN_COMPLETABLE_PAGES, this.fetchPages)
  },

  computed: {
    ...mapGetters("pages", [
      "getPageSequence",
      "getPageProgress",
      "getPageAccess"
    ]),

    homePage() {
      return this.sequence && this.sequence.homePage
    },

    homePageTitle() {
      return this.homePage && this.homePage.title
    },

    homePagePath() {
      return this.homePage && this.homePage.path
    },

    sequenceList() {
      return (
        this.sequence && this.sequence.list.filter(page => page.is_completable)
      )
    },

    sequenceAccessIsLoaded() {
      return this.sequenceList.every(page =>
        isPresent(this.getPageAccess(page.page_id))
      )
    }
  },

  methods: {
    isPresent,

    ...mapActions("pages", ["fetchPageSubscribers", "fetchPageSequenceAccess"]),

    fetchPages(rootId) {
      const sequence = this.getPageSequence(rootId)
      const progress = this.getPageProgress(rootId)

      this.handleShowModal()

      if (isPresent(sequence)) {
        this.sequence = sequence
        this.progress = progress

        this.ensureSequenceAccessLoaded(rootId)

        return
      }

      this.isLoading = true

      this.fetchPageSubscribers(rootId)
        .then(res => {
          this.sequence = {
            homePage: {
              title: res.page_title,
              path: res.page_path
            },
            list: res.sequence
          }

          this.progress = res.progress

          this.ensureSequenceAccessLoaded(rootId)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    handleShowModal() {
      this.$router.push(COMPLETABLE_PAGES_MODAL_HASH)
      this.showModal = true
    },

    ensureSequenceAccessLoaded(rootId) {
      if (!this.sequenceAccessIsLoaded) {
        this.fetchPageSequenceAccess(rootId)
      }
    },

    goToPath(path) {
      this.$router.replace(path)
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.completable-pages-modal {
  .v-modal__content {
    max-width: 400px;
  }

  .overview {
    font-weight: bold;
    border-bottom: 1px solid $color-ui--grey-30;
  }

  .page-progress {
    font-weight: $font-weight--semi-bold;
    color: $color-ui--black;
    border-bottom: 1px solid $color-ui--grey-20;

    &.page-home .v-icon {
      color: $color-ui--grey-60;
    }

    &:hover {
      background-color: $color-ui--grey-10;
    }
  }
}
</style>
